import { useState } from "react";
import { createPortal } from "react-dom";
import { X, RefreshCw, FileText, ExternalLink, Loader2 } from "lucide-react";
import api, { fmtINR, formatDetail } from "@/lib/api";
import { VendorResponse } from "@/components/payment/VendorResponse";
import { CallbackLog } from "@/components/payment/PayinHistoryExtras";

export const BUCKET_META = {
    in_progress: { label: "In progress", cls: "bg-amber-100 text-amber-800" },
    success: { label: "Success", cls: "bg-emerald-100 text-emerald-800" },
    failed: { label: "Failed", cls: "bg-rose-100 text-rose-800" },
    cancelled: { label: "Cancelled", cls: "bg-slate-200 text-slate-700" },
};

export function StatusPill({ order, testid }) {
    const m = BUCKET_META[order.status_bucket] || BUCKET_META.in_progress;
    return <span data-testid={testid} title={order.status} className={`px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${m.cls}`}>{m.label}</span>;
}

const Item = ({ k, v, mono }) => (
    <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-slate-400">{k}</div>
        <div className={`text-xs text-slate-800 break-all ${mono ? "font-mono" : ""}`}>{v ?? v === 0 ? v : "—"}</div>
    </div>
);

export function PayinOrderDrawer({ order, api: paths, isAdmin, partnerName, onClose, onUpdated }) {
    const [o, setO] = useState(order);
    const [busy, setBusy] = useState("");
    const [err, setErr] = useState("");
    const bucket = o.status_bucket || bucketGuess(o.status);

    const refreshStatus = async () => {
        setBusy("verify"); setErr("");
        try {
            const r = await api.post(paths.verify(o.order_id));
            const next = { ...o, ...(r.data.order || r.data) };
            next.status_bucket = bucketGuess(next.status);
            setO(next); onUpdated?.();
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setBusy(""); }
    };
    const receipt = async () => {
        setBusy("receipt"); setErr("");
        try {
            const r = await api.get(paths.receipt(o.order_id), { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
            window.open(url, "_blank"); setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setBusy(""); }
    };

    return createPortal(
        <div className="fixed inset-0 z-[100] flex justify-end bg-black/30" onClick={onClose}>
            <aside onClick={(e) => e.stopPropagation()} data-testid="payin-drawer"
                   className="w-full max-w-lg h-full bg-white shadow-2xl overflow-y-auto">
                <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-4 flex items-start justify-between gap-3 z-10">
                    <div className="min-w-0">
                        <div className="text-2xl font-bold font-mono" data-testid="payin-drawer-amount">{fmtINR(o.amount)}</div>
                        <div className="flex items-center gap-2 mt-1">
                            <StatusPill order={{ ...o, status_bucket: bucket }} testid="payin-drawer-status" />
                            <span className="text-[10px] font-mono text-slate-400">{o.status}</span>
                            {o.flow && <span data-testid="payin-drawer-flow" className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-semibold">{o.flow}</span>}
                            {o.response_sig === "mismatch" && (
                                <span data-testid="payin-drawer-sig-mismatch" title="Payspher's response_signature on order creation did not match our computation"
                                      className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 font-semibold">signature mismatch</span>
                            )}
                        </div>
                    </div>
                    <button onClick={onClose} data-testid="payin-drawer-close" className="p-1.5 rounded hover:bg-slate-100 text-slate-500"><X className="w-4 h-4" /></button>
                </div>

                <div className="p-5 space-y-5">
                    {o.response_message && (bucket === "failed" || bucket === "cancelled") && (
                        <div className="bg-rose-50 border border-rose-200 rounded-md px-3 py-2 text-xs text-rose-800" data-testid="payin-drawer-reason">{o.response_message}</div>
                    )}
                    {err && <div className="bg-rose-50 border border-rose-200 rounded px-3 py-2 text-xs text-rose-700" data-testid="payin-drawer-error">{err}</div>}

                    <div className="flex flex-wrap gap-2">
                        {bucket === "in_progress" && (
                            <button onClick={refreshStatus} disabled={!!busy} data-testid="payin-drawer-verify"
                                    className="px-3 py-1.5 rounded-md border border-slate-200 text-xs font-semibold flex items-center gap-1.5 hover:bg-slate-50 disabled:opacity-50">
                                {busy === "verify" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Refresh status
                            </button>
                        )}
                        {bucket === "in_progress" && o.payment_link && (
                            <a href={o.payment_link} target="_blank" rel="noreferrer" data-testid="payin-drawer-link"
                               className="px-3 py-1.5 rounded-md border border-slate-200 text-xs font-semibold flex items-center gap-1.5 hover:bg-slate-50">
                                <ExternalLink className="w-3.5 h-3.5" /> Payment link
                            </a>
                        )}
                        {bucket === "success" && (
                            <button onClick={receipt} disabled={!!busy} data-testid="payin-drawer-receipt"
                                    className="px-3 py-1.5 rounded-md bg-slate-900 text-white text-xs font-semibold flex items-center gap-1.5 hover:bg-slate-800 disabled:opacity-50">
                                {busy === "receipt" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />} Receipt
                            </button>
                        )}
                    </div>

                    <section className="grid grid-cols-2 gap-x-4 gap-y-3" data-testid="payin-drawer-details">
                        <Item k="Order ID" v={o.order_id} mono />
                        <Item k="Reference" v={o.client_reference_id} mono />
                        <Item k="Vendor payment ID" v={o.payspher_payment_id || null} mono />
                        <Item k="UTR" v={o.utr} mono />
                        <Item k="Method · flow" v={`${o.method || "—"} · ${o.flow || "—"}`} />
                        <Item k="Submerchant" v={o.submerchant_label} />
                        <Item k="Customer" v={o.customer_name} />
                        <Item k="Mobile" v={o.customer_mobile} mono />
                        <Item k="Email" v={o.customer_email} />
                        {isAdmin && <Item k="Partner" v={partnerName ? `${partnerName} (${o.partner_id})` : o.partner_id} />}
                        {isAdmin && <Item k="MID" v={o.mid_id} mono />}
                        <Item k="Created" v={new Date(o.created_at).toLocaleString()} />
                        <Item k="Updated" v={o.updated_at ? new Date(o.updated_at).toLocaleString() : null} />
                    </section>

                    {o.commission_amount != null && (
                        <section className="grid grid-cols-3 gap-3 bg-slate-50 border border-slate-100 rounded-lg p-3" data-testid="payin-drawer-amounts">
                            <Item k="Amount" v={fmtINR(o.amount)} mono />
                            <Item k={`Commission${o.commission_percent != null ? ` (${+o.commission_percent}%)` : ""}${o.commission_estimated ? " · est." : ""}`} v={`− ${fmtINR(o.commission_amount)}`} mono />
                            <Item k={o.commission_estimated ? "Net if captured" : "Net credited"} v={fmtINR(o.net_amount)} mono />
                        </section>
                    )}

                    {paths.callbacks && <CallbackLog orderId={o.order_id} paths={paths} />}

                    <VendorResponse order={o} testid="payin-drawer-vr"
                                    loadFull={isAdmin && paths.fullLog ? async () => (await api.get(paths.fullLog(o.order_id))).data : undefined} />
                </div>
            </aside>
        </div>, document.body);
}

const bucketGuess = (s) => {
    s = (s || "").toUpperCase();
    if (["CAPTURED", "SUCCESS"].includes(s)) return "success";
    if (["FAILED", "DECLINED", "REJECTED"].includes(s)) return "failed";
    if (["CANCELLED", "CANCELED", "EXPIRED", "ABANDONED"].includes(s)) return "cancelled";
    return "in_progress";
};
export { bucketGuess };
